const apiUrl =
  process.env.GATSBY_API_URL || `https://business.api.staging.fena.co`;

const consumerApiUrl =
  process.env.GATSBY_CONSUMER_API_URL || `https://consumer.api.fena.co`;

// const apiUrl = `http://localhost:8084`;

class Api {
  private readonly apiUrl: string;

  private readonly consumerApiUrl: string;

  constructor(url, consumerUrl) {
    this.apiUrl = url;
    this.consumerApiUrl = consumerUrl;
  }

  public async getProviderList(
    type = `merchant`,
    excludedBanks: Array<string> = [],
    enabled = true,
  ) {
    const res = await fetch(
      `${
        type === `merchant` ? this.apiUrl : this.consumerApiUrl
      }/providers/list?enabled=${enabled}${
        excludedBanks?.length > 0
          ? `&excluded=${JSON.stringify(excludedBanks)}`
          : ``
      }`,
      {
        method: `GET`,
      },
    );
    return res.json();
  }

  public async getPaymentInfo(paymentId, type = `merchant`) {
    const res = await fetch(
      `${
        type === `merchant` ? this.apiUrl : this.consumerApiUrl
      }/payment-flow/public/payment/${paymentId}`,
      {
        method: `GET`,
      },
    );
    return res.json();
  }

  public async getInvoiceInfo(invoiceId) {
    const res = await fetch(
      `${this.apiUrl}/payment-flow/public/invoice/${invoiceId}`,
      {
        method: `GET`,
      },
    );
    return res.json();
  }

  public async getPaymentRefundInfo(paymentId, type = `merchant`) {
    const res = await fetch(
      `${
        type === `merchant` ? this.apiUrl : this.consumerApiUrl
      }/payment-flow/public/refund/payment/${paymentId}`,
      {
        method: `GET`,
      },
    );
    return res.json();
  }

  public async getInvoiceRefundInfo(invoiceId) {
    const res = await fetch(
      `${this.apiUrl}/payment-flow/public/refund/invoice/${invoiceId}`,
      {
        method: `GET`,
      },
    );
    return res.json();
  }

  public async initiatePayment(id, type, provider, p2p = false) {
    console.log(`starting initiation`);
    const res = await fetch(
      `${!p2p ? this.apiUrl : this.consumerApiUrl}/payment-flow/initiate`,
      {
        method: `POST`,
        headers: {
          Accept: `application/json`,
          'Content-Type': `application/json`,
        },
        body: JSON.stringify({
          type,
          id,
          provider,
        }),
      },
    );
    return res.json();
  }

  public async initiateRefund(id, type, provider) {
    console.log(`starting initiation`);
    const res = await fetch(`${this.apiUrl}/payment-flow/initiate-refund`, {
      method: `POST`,
      headers: {
        Accept: `application/json`,
        'Content-Type': `application/json`,
      },
      body: JSON.stringify({
        type,
        id,
        provider,
      }),
    });
    return res.json();
  }

  public async getLastPaymentByTerminal(terminalId) {
    const res = await fetch(
      `${this.apiUrl}/payment-flow/public/terminal/${terminalId}`,
      {
        method: `GET`,
      },
    );
    return res.json();
  }

  public async manuallyGetStatusFromProvider(
    providerId: string,
    type = `merchant`,
  ) {
    const res = await fetch(
      `${
        type === `merchant` ? this.apiUrl : this.consumerApiUrl
      }/payment-flow/manual/${providerId}`,
      {
        method: `GET`,
      },
    );
    return res.json();
  }

  public async createDataAPIConnection(companyId: string, providerId: string) {
    const res = await fetch(`${this.apiUrl}/payment-flow/public/connect`, {
      method: `POST`,
      headers: {
        Accept: `application/json`,
        'Content-Type': `application/json`,
      },
      body: JSON.stringify({
        externalId: providerId,
        userId: companyId,
        redirectUrl: `${window.location.origin}/connectionstatus/${companyId}/`,
      }),
    });
    return res.json();
  }

  public async getRecurringPaymentInfo(rpId: string) {
    const res = await fetch(
      `${this.apiUrl}/payment-flow/public/recurring-payment/${rpId}`,
      {
        method: `GET`,
      },
    );
    return res.json();
  }

  public async getRecurringPaymentByInitialPayment(initialPaymentId: string) {
    const res = await fetch(
      `${this.apiUrl}/payment-flow/public/recurring-payment/${initialPaymentId}/get-by-initial`,
      {
        method: `GET`,
      },
    );
    return res.json();
  }

  public async manualCheckRPstatus(customerPaymentId: string) {
    const res = await fetch(
      `${this.apiUrl}/payment-flow/public/recurring-payment/status/${customerPaymentId}`,
      {
        method: `GET`,
      },
    );
    return res.json();
  }

  public async getPublicCompanyData(companyId: string) {
    const res = await fetch(
      `${this.apiUrl}/payment-flow/public/${companyId}/company`,
      {
        method: `GET`,
      },
    );
    return res.json();
  }

  public async getBankAccountsForCompany(companyId, consentId) {
    const url = new URL(
      `${this.apiUrl}/payment-flow/public/${companyId}/bank-accounts/`,
    );
    url.searchParams.append(`field`, `consentID`);
    url.searchParams.append(`value`, consentId);
    const res = await fetch(url.toString(), {
      method: `GET`,
    });
    return res.json();
  }

  public async getPublicBankAccountData(bankAccountId: string) {
    const res = await fetch(
      `${this.apiUrl}/payment-flow/public/bank-account/${bankAccountId}`,
      {
        method: `GET`,
      },
    );

    const data = await res.json();
    return data.data;
  }

  async reconsentBankAccount(bankAccountId: string) {
    const url = new URL(
      `${this.apiUrl}/payment-flow/public/bank-account/${bankAccountId}/reconsent`,
    );
    const result = await fetch(url.toString(), {
      method: `POST`,
    });
    const { status } = result;
    return status;
  }

  async fullReconsentBankAccount(bankAccountId: string, companyId: string) {
    const url = new URL(
      `${this.apiUrl}/payment-flow/public/bank-account/${bankAccountId}/full-reconsent`,
    );
    const body = JSON.stringify({
      redirectUrl: `${window.location.origin}/reconsentstatus/${bankAccountId}/`,
      companyId,
    });
    const result = await fetch(url.toString(), {
      method: `POST`,
      headers: {
        Accept: `application/json`,
        'Content-Type': `application/json`,
      },
      body,
    });

    const data = await result.json();
    return data.data;
  }
}

export default new Api(apiUrl, consumerApiUrl);
